import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowRight } from "lucide-react";
import WhatsAppFab from "./WhatsAppFab";
import "./LiveLayout.css";

/* ============================================================
   LiveLayout — production shell for the two audience journeys.
   Header nav now NAVIGATES to dedicated pages (NavLink routes),
   not scroll-spy anchors. One persistent header per audience;
   only the active route changes. A single CTA button per audience.
   ============================================================ */

const NAV = {
  employer: {
    items: [
      { label: "Overview", to: "/employers", end: true },
      { label: "How it works", to: "/employers/how-it-works" },
      { label: "Why Adel", to: "/employers/why-adel" },
      { label: "Sectors", to: "/employers/sectors" },
    ],
    cta: { label: "Request workers", to: "/employers/brief" },
  },
  candidate: {
    items: [
      { label: "Overview", to: "/candidates", end: true },
      { label: "Your journey", to: "/candidates/journey" },
      { label: "Sectors", to: "/candidates/sectors" },
      { label: "Jobs", to: "/candidates/jobs" },
    ],
    cta: { label: "Submit CV", to: "/candidates/apply" },
  },
};

export default function LiveLayout({ children, audience }) {
  const nav = NAV[audience] || NAV.candidate;
  const otherKey  = audience === "employer" ? "candidate" : "employer";
  const otherPath = audience === "employer" ? "/candidates" : "/employers";
  const otherSub  = audience === "employer" ? "Looking for work?" : "Looking to hire?";
  const otherCta  = audience === "employer" ? "Find a job" : "Hire workforce";
  const tagline = audience === "employer"
    ? <>We source. <span className="ll__tagline-accent">You choose.</span></>
    : <>Connecting <span className="ll__tagline-accent">Talents.</span></>;

  const [open, setOpen] = useState(false);

  /* Lock body scroll while the mobile overlay is open */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const navClass = ({ isActive }) => `ll__section ${isActive ? "is-active" : ""}`;

  return (
    <div className="ll">
      <header className="ll__header">
        <div className="ll__header-inner">
          <Link to="/" className="ll__logo" aria-label="Adel Group Kenya — home">
            <img src="/adel-logo-light.png" alt="Adel Group" width="67" height="36" />
          </Link>

          {/* Menu button — leads the bar on mobile */}
          <button
            type="button"
            className="ll__burger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
          </button>

          {/* Section routes — desktop */}
          <nav className="ll__sections" aria-label="Sections">
            {nav.items.map((s) => (
              <NavLink key={s.to} to={s.to} end={s.end} className={navClass}>
                {s.label}
              </NavLink>
            ))}
          </nav>

          {/* Audience tagline — trails the bar on mobile */}
          <span className="ll__tagline">{tagline}</span>

          {/* Right: phone + audience switch + CTA (desktop) */}
          <div className="ll__right">
            <a href="tel:+254117222777" className="ll__phone" aria-label="Call Adel Group">
              <Phone size={14} strokeWidth={2.2} />
              <span>+254 117 222 777</span>
            </a>
            <Link to={otherPath} className="ll__switch">
              <span className="ll__switch-sub">{otherSub}</span>
              <span className="ll__switch-cta">{otherCta} →</span>
            </Link>
            <Link to={nav.cta.to} className="ll__cta">
              {nav.cta.label} <ArrowRight size={14} strokeWidth={2.4} />
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="ll__overlay"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            data-key={otherKey}
          >
            <nav className="ll__overlay-nav" aria-label="Mobile navigation">
              {nav.items.map((s, i) => (
                <motion.div key={s.to}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.32, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}>
                  <NavLink to={s.to} end={s.end} onClick={() => setOpen(false)}>
                    {s.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link to={nav.cta.to} className="ll__overlay-cta" onClick={() => setOpen(false)}>
                {nav.cta.label} <ArrowRight size={16} strokeWidth={2.4} />
              </Link>
              <Link to={otherPath} className="ll__overlay-switch" onClick={() => setOpen(false)}>
                <span>{otherSub}</span>
                <strong>{otherCta} →</strong>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {children}
      <WhatsAppFab audience={audience} />
    </div>
  );
}
